import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight, ExternalLink, PenLine, Star } from "lucide-react";
import { GOOGLE_PROFILE_URL } from "../../lib/homeCopy";
import { LOCALES } from "../../lib/bookingMessage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  de: { title: "Was unsere Kunden sagen.", text: "Echte Bewertungen aus unserem Google-Unternehmensprofil.", all: "Alle Bewertungen auf Google", write: "Bewertung schreiben", more: "Weiterlesen", less: "Weniger", count: "Bewertungen", asOf: "Stand", source: "Google", manual: "Aus unserem Google-Profil übernommen. Originaltext der Kunden.", empty: "Lesen Sie unabhängige Bewertungen unserer Kunden direkt auf Google.", prev: "Vorherige Bewertungen", next: "Nächste Bewertungen", view: "Auf Google ansehen" },
  ua: { title: "Що кажуть наші клієнти.", text: "Справжні відгуки з нашого профілю компанії в Google.", all: "Усі відгуки в Google", write: "Залишити відгук", more: "Читати далі", less: "Згорнути", count: "відгуків", asOf: "Станом на", source: "Google", manual: "Перенесено з нашого профілю Google. Оригінальний текст клієнтів.", empty: "Читайте незалежні відгуки наших клієнтів безпосередньо в Google.", prev: "Попередні відгуки", next: "Наступні відгуки", view: "Переглянути в Google" },
  en: { title: "What our customers say.", text: "Genuine reviews from our Google Business Profile.", all: "All reviews on Google", write: "Leave a review", more: "Read more", less: "Show less", count: "reviews", asOf: "As of", source: "Google", manual: "Taken from our Google profile. Original customer wording.", empty: "Read independent reviews from our customers directly on Google.", prev: "Previous reviews", next: "Next reviews", view: "View on Google" },
  pl: { title: "Co mówią nasi klienci.", text: "Prawdziwe opinie z naszego profilu firmy w Google.", all: "Wszystkie opinie w Google", write: "Wystaw opinię", more: "Czytaj dalej", less: "Zwiń", count: "opinii", asOf: "Stan na", source: "Google", manual: "Przeniesione z naszego profilu Google. Oryginalna treść klientów.", empty: "Przeczytaj niezależne opinie naszych klientów bezpośrednio w Google.", prev: "Poprzednie opinie", next: "Następne opinie", view: "Zobacz w Google" },
  ru: { title: "Что говорят наши клиенты.", text: "Настоящие отзывы из нашего профиля компании в Google.", all: "Все отзывы в Google", write: "Оставить отзыв", more: "Читать далее", less: "Свернуть", count: "отзывов", asOf: "По состоянию на", source: "Google", manual: "Перенесено из нашего профиля Google. Оригинальный текст клиентов.", empty: "Читайте независимые отзывы наших клиентов прямо в Google.", prev: "Предыдущие отзывы", next: "Следующие отзывы", view: "Открыть в Google" },
};
export const reviewsCopy = (lang) => {
  const c = COPY[lang] || COPY.de;
  return { ...c, live: LIVE[lang] || LIVE.de };
};
const LIVE = { de: "Bewertungen von Google", ua: "Відгуки з Google", en: "Reviews from Google", pl: "Opinie z Google", ru: "Отзывы из Google" };

const fmtDate = (v, lang) => {
  if (!v) return "";
  const d = new Date(v.length === 7 ? `${v}-01T12:00:00` : v.length === 10 ? `${v}T12:00:00` : v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(LOCALES[lang] || "de-DE", v.length === 7 ? { month: "long", year: "numeric" } : { day: "numeric", month: "long", year: "numeric" });
};

const Stars = ({ n, size = 14 }) => (
  <span className="inline-flex gap-0.5" role="img" aria-label={`${n} / 5`}>
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={size} className={i <= n ? "text-[#cdb27a]" : "text-zinc-700"} fill="currentColor" strokeWidth={0} />)}
  </span>
);

const ReviewCard = ({ r, c, lang, i }) => {
  const [open, setOpen] = useState(false);
  const long = r.text.length > 260;
  return (
    <article data-testid={`review-card-${i}`} className="snap-start shrink-0 w-[86%] sm:w-[70%] md:w-[calc((100%-1.25rem)/2)] lg:w-[calc((100%-2.5rem)/3)] flex flex-col rounded-[20px] bg-[#111113] border border-white/[0.07] p-7 md:p-8 transition-colors duration-300 hover:border-white/[0.14]">
      <div className="flex items-center justify-between gap-4">
        <Stars n={Math.round(r.rating || 0)} />
        <span className="text-[11px] tracking-[0.2em] uppercase text-zinc-500">{c.source}</span>
      </div>
      <p className={`mt-6 text-[15px] md:text-[16px] text-zinc-200 leading-relaxed whitespace-pre-line ${long && !open ? "line-clamp-6" : ""}`}>{r.text}</p>
      {long && (
        <button type="button" data-testid={`review-readmore-${i}`} aria-expanded={open} onClick={() => setOpen((v) => !v)}
          className="mt-3 self-start text-[13px] text-[#cdb27a] hover:text-white transition-colors">
          {open ? c.less : c.more}
        </button>
      )}
      <footer className="mt-auto pt-7 flex items-end justify-between gap-4">
        <div className="min-w-0">
          <div data-testid={`review-author-${i}`} className="flex items-center gap-2.5 text-[15px] text-white min-w-0">
            {r.authorPhotoUri && <img src={r.authorPhotoUri} alt="" width="28" height="28" loading="lazy" referrerPolicy="no-referrer" className="w-7 h-7 rounded-full shrink-0" />}
            {r.authorUri ? <a href={r.authorUri} target="_blank" rel="noopener noreferrer" className="truncate hover:text-[#cdb27a] transition-colors">{r.authorName}</a> : <span className="truncate">{r.authorName}</span>}
          </div>
          {(r.reviewDate || r.publishTime || r.relativeTime) && (
            <div className="mt-1 text-[12px] text-zinc-500">{fmtDate(r.reviewDate || r.publishTime, lang) || r.relativeTime}</div>
          )}
        </div>
        {r.googleMapsUri && (
          <a href={r.googleMapsUri} target="_blank" rel="noopener noreferrer" aria-label={c.view} className="shrink-0 text-zinc-500 hover:text-[#cdb27a] transition-colors">
            <ExternalLink size={14} />
          </a>
        )}
      </footer>
    </article>
  );
};

const Arrow = ({ dir, onClick, label }) => (
  <button type="button" aria-label={label} data-testid={`reviews-${dir}`} onClick={onClick}
    className="hidden md:inline-flex w-11 h-11 rounded-full items-center justify-center border border-white/12 text-zinc-300 hover:border-white/40 hover:text-white transition-colors">
    {dir === "prev" ? <ChevronLeft size={17} /> : <ChevronRight size={17} />}
  </button>
);

export const useGoogleReviews = (lang) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let off = false;
    fetch(`${BACKEND_URL}/api/reviews/google?lang=${lang}`).then((r) => r.json()).then((d) => !off && setData(d)).catch(() => !off && setData({ configured: false, reviews: [] }));
    return () => { off = true; };
  }, [lang]);
  return data;
};

const RatingSummary = ({ data, c, lang }) =>
  data?.rating ? (
    <div data-testid="reviews-rating" className="flex items-center gap-4">
      <span className="font-heading text-white text-5xl leading-none tracking-tight">{Number(data.rating).toFixed(1)}</span>
      <span className="flex flex-col gap-1.5">
        <Stars n={Math.round(data.rating)} size={15} />
        <span className="text-[13px] text-zinc-400">
          {data.userRatingCount ? `${data.userRatingCount} ${c.count} · ` : ""}Google
          {data.ratingAsOf ? ` · ${c.asOf} ${fmtDate(data.ratingAsOf, lang)}` : ""}
        </span>
      </span>
    </div>
  ) : null;

export const GoogleReviewsSection = ({ lang }) => {
  const c = reviewsCopy(lang);
  const data = useGoogleReviews(lang);
  const reduce = useReducedMotion();
  const track = useRef(null);
  const reviews = data?.reviews || [];
  const scroll = (d) => {
    const el = track.current;
    if (!el) return;
    const card = el.querySelector("article");
    el.scrollBy({ left: d * ((card?.offsetWidth || el.clientWidth) + 20), behavior: reduce ? "auto" : "smooth" });
  };

  return (
    <section id="reviews" data-testid="chapter-trust" data-chapter="7" className="bg-[#050505] py-20 md:py-28 border-t border-white/[0.06]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-end">
          <div className="lg:col-span-7">
            <span data-testid="chapter-trust-label" className="text-[12px] tracking-[0.35em] uppercase text-[#cdb27a]">Google Reviews</span>
            <h2 className="mt-5 font-heading text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">{c.title}</h2>
            <p className="mt-5 text-zinc-400 text-[15px] md:text-[17px] leading-relaxed max-w-xl">{c.text}</p>
          </div>
          <div className="lg:col-span-5 lg:justify-self-end"><RatingSummary data={data} c={c} lang={lang} /></div>
        </div>

        {reviews.length > 0 ? (
          <>
            <div ref={track} data-testid="reviews-carousel" aria-roledescription="carousel"
              className="mt-12 md:mt-14 flex gap-5 overflow-x-auto snap-x snap-mandatory overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden items-stretch">
              {reviews.map((r, i) => <ReviewCard key={`${r.authorName}-${i}`} r={r} c={c} lang={lang} i={i} />)}
            </div>
            <p data-testid="reviews-source-note" className="mt-5 text-[12px] text-zinc-500">{data.source === "google_api" ? c.live : c.manual}</p>
          </>
        ) : (
          <p data-testid="reviews-fallback" className="mt-12 text-[16px] text-zinc-300 max-w-xl">{c.empty}</p>
        )}

        <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-3">
          <a href={data?.googleMapsUri || GOOGLE_PROFILE_URL} target="_blank" rel="noopener noreferrer" data-testid="trust-google-link"
            className="inline-flex items-center justify-center gap-3 rounded-full bg-white text-black px-7 py-4 text-[12px] tracking-[0.2em] uppercase font-semibold hover:bg-[#cdb27a] transition-colors duration-300">
            {c.all} <ExternalLink size={13} />
          </a>
          {data?.writeReviewUri && (
            <a href={data.writeReviewUri} target="_blank" rel="noopener noreferrer" data-testid="reviews-write-link"
              className="inline-flex items-center justify-center gap-3 rounded-full border border-white/20 text-white px-7 py-4 text-[12px] tracking-[0.2em] uppercase hover:border-white/50 transition-colors duration-300">
              <PenLine size={13} /> {c.write}
            </a>
          )}
          {reviews.length > 1 && (
            <div className="sm:ml-auto flex gap-2">
              <Arrow dir="prev" onClick={() => scroll(-1)} label={c.prev} />
              <Arrow dir="next" onClick={() => scroll(1)} label={c.next} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
